import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    inject,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { NewsFeedItemContentBase } from '../news-feed-item-content-base';
import { RollupContentComponent } from '@fi-shared/rollup/rollup-content.component';
import { Subject } from 'rxjs';
import 'rxjs-compat/add/operator/takeUntil';
import { INewsFeedVisibleArgs } from '@fi-shared/fi-news-feed-shared/fi-news-feed-visibility-helper/fi-news-feed-visibility-helper.component';
import { TextEditorService } from '@fi-shared/fi-text-editor/text-editor.service';

@Component({
    selector: 'fi-news-feed-item-content-post',
    templateUrl: './news-feed-item-content-post.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./news-feed-item-content-post.component.scss'],
    host: { class: 'fi-news-feed-item-content-post' },
    standalone: false,
})
export class NewsFeedItemContentPostComponent
    extends NewsFeedItemContentBase
    implements AfterViewInit, OnInit, OnDestroy
{
    @Input() highlightText: string;
    @Input() showWithContentOpened;
    @Input() afterSavePost$: Subject<void> = new Subject<void>();

    @ViewChild(RollupContentComponent, { static: false }) rollupContent: RollupContentComponent;
    @Output() becomeVisible = new EventEmitter<INewsFeedVisibleArgs>();
    @Output() becomeInvisible = new EventEmitter();

    private textEditorService = inject(TextEditorService);

    public ngOnInit(): void {
        // subscribe to afterSavePost and recalculate height in RollupContentComponent
        this.afterSavePost$.takeUntil(this.ngOnDestroy$).subscribe(() => {
            this.cdr.markForCheck();
            this.rollupContent.recalculateMinHeight();
        });
    }

    public ngAfterViewInit(): void {
        if (!this.isLongPost()) {
            this.becomeVisible.emit();
        }

        if (this.showWithContentOpened) {
            this.rollupContent.openContent();
        }
    }

    protected getContent(): string {
        // https://chainformation.atlassian.net/browse/FI-2788
        // we should replace only text, not html tags and attributes
        return this.highlightService.getHighlightLayout(this.newsItem.Content.Body);
    }

    protected onContentOpened(): void {
        this.becomeVisible.emit({ isLongPost: true });
    }

    protected onContentClosed(): void {
        this.becomeInvisible.emit();
    }

    protected isPostTitleShown(): boolean {
        return !!this.newsItem.Content.Title;
    }

    private isLongPost(): boolean {
        return this.rollupContent.canExpand$.value;
    }

    protected getContentBody(): string {
        return this.newsItem.Content.Body || '';
    }

    public isAttachmentListShown(): boolean {
        return !!this.newsItem.Links.length || !!this.newsItemMediaBankReferences.length;
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.highlightService.setHighlightText(undefined);
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
